
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from "react-i18next";
import { Loader2 } from 'lucide-react';
import { AuthShell, AUTH_HERO_GRADIENT, AUTH_INPUT, AUTH_LABEL, AUTH_SUBMIT } from './AuthShell';
import { resolveStaffLoginEmail } from '@/lib/staffLogin';

export const AuthPage = () => {
  const { signIn, user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
  });

  // When a staff username exists in several salons, the user types which salon
  // (we never reveal the salon list to an unauthenticated caller).
  const [needsSalon, setNeedsSalon] = useState(false);
  const [salonInput, setSalonInput] = useState('');

  const [signupForm, setSignupForm] = useState({
    studioName: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
    referralCode: '',
    acceptedTerms: false,
  });

  // Prefill the referral code from a ?ref=CODE link (referral program).
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setSignupForm((prev) => ({ ...prev, referralCode: ref.trim().toUpperCase() }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: AUTH_HERO_GRADIENT }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Resolve the identifier to an email: real emails pass through; staff
    // usernames are looked up (and may need a salon pick on collision).
    const resolved = await resolveStaffLoginEmail(
      loginForm.email,
      salonInput.trim() || undefined,
    );

    if (resolved.kind === 'needsSalon') {
      setNeedsSalon(true);
      toast({
        title: t('loginFailed'),
        description: t('staffLogin.pickSalon'),
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    if (resolved.kind === 'notFound') {
      // Generic message — never reveal whether a username exists.
      toast({
        title: t('loginFailed'),
        description: t('staffLogin.invalidCredentials'),
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    const { error } = await signIn(resolved.email, loginForm.password);

    if (error) {
      toast({
        title: t('loginFailed'),
        description: error.message,
        variant: 'destructive',
      });
    } else {
      setNeedsSalon(false);
      navigate('/dashboard');
    }

    setLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signupForm.studioName.trim()) {
      toast({
        title: t('signupFailed'),
        description: t('referral.signupStudioRequired'),
        variant: 'destructive',
      });
      return;
    }

    if (signupForm.password !== signupForm.confirmPassword) {
      toast({
        title: t('passwordMismatch'),
        description: t('passwordsMustMatch'),
        variant: 'destructive',
      });
      return;
    }

    if (!signupForm.acceptedTerms) {
      toast({
        title: t('signupFailed'),
        description: t('referral.signupTermsRequired'),
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const refCode = signupForm.referralCode.trim();
      // Create the auth account + studio (tenant) in one go via the shared
      // create-tenant function, attributing the referral when a code is present.
      const { data, error } = await supabase.functions.invoke('create-tenant', {
        body: {
          name: signupForm.studioName.trim(),
          first_name: signupForm.firstName.trim(),
          last_name: signupForm.lastName.trim(),
          email: signupForm.email.trim(),
          password: signupForm.password,
          terms_opt_in: true,
          source: 'Self-Service Registrierung',
          ...(refCode ? { referral_code: refCode } : {}),
        },
      });

      // On a non-2xx, supabase-js sets `error.message` to a generic
      // "Edge Function returned a non-2xx status code"; the real, human-readable
      // reason lives in the function's JSON response body (error.context).
      let serverMessage = (data as { error?: string } | null)?.error || '';
      if (error) {
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const rawBody = await ctx.json();
            serverMessage = rawBody?.details || rawBody?.error || '';
          }
        } catch {
          // Couldn't parse the body — fall back to a generic message below.
        }
      }

      if (error || serverMessage) {
        const alreadyRegistered =
          /already.*(registered|exists)|bereits.*registriert/i.test(serverMessage);
        toast({
          title: t('signupFailed'),
          description: alreadyRegistered
            ? t('referral.signupEmailExists')
            : serverMessage || t('referral.signupError'),
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Account + studio created — sign the user in to start their session.
      const { error: signInError } = await signIn(signupForm.email.trim(), signupForm.password);
      if (signInError) {
        toast({ title: t('accountCreated'), description: t('referral.signupSignInHint') });
        setLoading(false);
        return;
      }
      navigate('/dashboard');
    } catch {
      toast({
        title: t('signupFailed'),
        description: t('referral.signupError'),
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  return (
    <AuthShell tagline={t('authTagline', { defaultValue: 'Der KI-Assistent für dein Studio.' })}>
      <Tabs defaultValue="login" className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-full bg-white/10 p-1">
          <TabsTrigger
            value="login"
            className="w-full rounded-full py-2 text-white/70 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
          >
            {t('login')}
          </TabsTrigger>
          <TabsTrigger
            value="signup"
            className="w-full rounded-full py-2 text-white/70 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
          >
            {t('signUp')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="login" className="mt-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className={AUTH_LABEL}>{t('staffLogin.emailOrUsername')}</Label>
              <Input
                id="login-email"
                type="text"
                value={loginForm.email}
                onChange={(e) => {
                  setLoginForm({ ...loginForm, email: e.target.value });
                  // Reset the salon prompt when the identifier changes.
                  if (needsSalon) { setNeedsSalon(false); setSalonInput(''); }
                }}
                autoComplete="username"
                required
                className={AUTH_INPUT}
              />
            </div>
            {needsSalon && (
              <div className="space-y-1.5">
                <Label htmlFor="login-salon" className={AUTH_LABEL}>{t('staffLogin.salon')}</Label>
                <Input
                  id="login-salon"
                  type="text"
                  value={salonInput}
                  onChange={(e) => setSalonInput(e.target.value)}
                  placeholder={t('staffLogin.enterSalon')}
                  required
                  className={AUTH_INPUT}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="login-password" className={AUTH_LABEL}>{t('password')}</Label>
                <button
                  type="button"
                  onClick={() => navigate('/auth/reset-password')}
                  className="text-xs text-white/55 transition-colors hover:text-white"
                >
                  {t('forgotPassword')}
                </button>
              </div>
              <Input
                id="login-password"
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                autoComplete="current-password"
                required
                className={AUTH_INPUT}
              />
            </div>
            <Button type="submit" className={AUTH_SUBMIT} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('login')}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="signup" className="mt-6">
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="signup-studio" className={AUTH_LABEL}>{t('referral.signupStudioName')}</Label>
              <Input
                id="signup-studio"
                value={signupForm.studioName}
                onChange={(e) => setSignupForm({ ...signupForm, studioName: e.target.value })}
                placeholder={t('referral.signupStudioNamePlaceholder')}
                autoComplete="organization"
                required
                className={AUTH_INPUT}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="signup-firstname" className={AUTH_LABEL}>{t('firstName')}</Label>
                <Input
                  id="signup-firstname"
                  value={signupForm.firstName}
                  onChange={(e) => setSignupForm({ ...signupForm, firstName: e.target.value })}
                  autoComplete="given-name"
                  className={AUTH_INPUT}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-lastname" className={AUTH_LABEL}>{t('lastName')}</Label>
                <Input
                  id="signup-lastname"
                  value={signupForm.lastName}
                  onChange={(e) => setSignupForm({ ...signupForm, lastName: e.target.value })}
                  autoComplete="family-name"
                  className={AUTH_INPUT}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-email" className={AUTH_LABEL}>{t('email')}</Label>
              <Input
                id="signup-email"
                type="email"
                value={signupForm.email}
                onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                autoComplete="email"
                required
                className={AUTH_INPUT}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-password" className={AUTH_LABEL}>{t('password')}</Label>
              <Input
                id="signup-password"
                type="password"
                value={signupForm.password}
                onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                autoComplete="new-password"
                required
                className={AUTH_INPUT}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-confirm-password" className={AUTH_LABEL}>{t('confirmPassword')}</Label>
              <Input
                id="signup-confirm-password"
                type="password"
                value={signupForm.confirmPassword}
                onChange={(e) => setSignupForm({ ...signupForm, confirmPassword: e.target.value })}
                autoComplete="new-password"
                required
                className={AUTH_INPUT}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-referral" className={AUTH_LABEL}>{t('referral.signupReferralCode')}</Label>
              <Input
                id="signup-referral"
                value={signupForm.referralCode}
                onChange={(e) => setSignupForm({ ...signupForm, referralCode: e.target.value.toUpperCase() })}
                placeholder={t('referral.signupReferralPlaceholder')}
                autoComplete="off"
                className={`${AUTH_INPUT} font-mono tracking-widest`}
              />
              {signupForm.referralCode && (
                <p className="text-xs text-white/55">{t('referral.signupReferralApplied')}</p>
              )}
            </div>
            <div className="flex items-start gap-2.5 pt-1">
              <Checkbox
                id="signup-terms"
                checked={signupForm.acceptedTerms}
                onCheckedChange={(checked) =>
                  setSignupForm({ ...signupForm, acceptedTerms: checked === true })
                }
                className="mt-0.5 border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-primary"
              />
              <Label htmlFor="signup-terms" className="text-sm font-normal leading-snug text-white/75">
                {t('referral.signupAcceptTerms')}
              </Label>
            </div>
            <Button type="submit" className={AUTH_SUBMIT} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('createAccount')}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </AuthShell>
  );
};
